import { useState, useEffect, useCallback } from 'react';
import { Bell, Check, ExternalLink, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useQuery, useMutation } from '@/lib/hooks/use-query';
import {
  GET_UNSEEN_NOTIFICATION_COUNT,
  GET_STAFF_NOTIFICATIONS,
  MARK_NOTIFICATIONS_SEEN,
  MARK_NOTIFICATION_READ,
  DISMISS_NOTIFICATION,
} from '@/lib/graphql/operations/notifications';
import { useTranslations } from '@/lib/i18n/client';
import { useI18nSafe } from '@/lib/i18n/i18n-context';
import Link from '@/lib/compat/link';

interface StaffNotification {
  id: string;
  type: string;
  severity: string;
  title: string;
  body: string | null;
  actionUrl: string | null;
  metadata: Record<string, unknown>;
  seenAt: string | null;
  readAt: string | null;
  createdAt: string;
}

const POLL_INTERVAL = 30_000; // 30 seconds

const SEVERITY_COLORS: Record<string, string> = {
  info: 'bg-blue-500',
  warning: 'bg-amber-500',
  critical: 'bg-red-500',
};

const TYPE_ICONS: Record<string, string> = {
  new_order: '🛒',
  order_cancelled: '❌',
  order_comment_mention: '💬',
  order_assigned: '👤',
  low_stock: '📦',
  return_requested: '🔄',
  product_question: '❓',
  weekly_digest: '📊',
};

export function NotificationBell() {
  const t = useTranslations();
  const { language } = useI18nSafe();
  const [open, setOpen] = useState(false);

  // Poll for unseen count every 30s
  const { data: countData, refetch: refetchCount } = useQuery<{
    unseenNotificationCount: number;
  }>(GET_UNSEEN_NOTIFICATION_COUNT, {});

  // Fetch notifications list only when popover opens
  const { data: listData, loading: listLoading, refetch: refetchList } = useQuery<{
    staffNotifications: { items: StaffNotification[]; totalCount: number };
  }>(GET_STAFF_NOTIFICATIONS, { variables: { options: { take: 10 } } }, { skip: !open });

  const { execute: markSeen } = useMutation(MARK_NOTIFICATIONS_SEEN);
  const { execute: markRead } = useMutation(MARK_NOTIFICATION_READ);
  const { execute: dismissNotif } = useMutation(DISMISS_NOTIFICATION);

  const unseenCount = countData?.unseenNotificationCount ?? 0;
  const notifications = listData?.staffNotifications?.items ?? [];

  // Poll for badge count
  useEffect(() => {
    const interval = setInterval(() => {
      refetchCount();
    }, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [refetchCount]);

  // Mark all as seen when popover opens
  const handleOpen = useCallback(async (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      refetchList();
      if (unseenCount > 0) {
        await markSeen({});
        // Wait a tick then refetch count to clear badge
        setTimeout(() => refetchCount(), 500);
      }
    }
  }, [unseenCount, markSeen, refetchCount, refetchList]);

  const handleNotificationClick = async (notification: StaffNotification) => {
    if (!notification.readAt) {
      await markRead({ id: notification.id });
    }
    if (notification.actionUrl) {
      setOpen(false);
    }
  };

  const handleDismiss = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    e.preventDefault();
    await dismissNotif({ id });
    refetchList();
  };

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return language === 'ar' ? 'الآن' : 'now';
    if (diffMins < 60) return language === 'ar' ? `${diffMins} د` : `${diffMins}m`;
    if (diffHours < 24) return language === 'ar' ? `${diffHours} س` : `${diffHours}h`;
    return language === 'ar' ? `${diffDays} ي` : `${diffDays}d`;
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={t('merchantNotifications.title')}
        >
          <Bell className="w-5 h-5 text-muted-foreground" />
          {unseenCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
              {unseenCount > 9 ? '9+' : unseenCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-96 p-0" sideOffset={8}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-sm font-semibold">{t('merchantNotifications.title')}</h3>
          <div className="flex items-center gap-2">
            {unseenCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {unseenCount} {t('merchantNotifications.new')}
              </Badge>
            )}
            {notifications.some(n => !n.readAt) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={async () => {
                  await markSeen({});
                  // Mark all as read by clicking each unread
                  for (const n of notifications.filter(n => !n.readAt)) {
                    await markRead({ id: n.id });
                  }
                  refetchList();
                  refetchCount();
                }}
              >
                <Check className="w-3 h-3 me-1" />
                {t('merchantNotifications.markAllRead')}
              </Button>
            )}
          </div>
        </div>

        {/* Notification List */}
        <div className="max-h-[400px] overflow-y-auto">
          {listLoading ? (
            <div className="p-4 space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex gap-3">
                  <Skeleton className="w-8 h-8 rounded-full shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Bell className="w-10 h-10 text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">{t('merchantNotifications.empty')}</p>
            </div>
          ) : (
            notifications.map((n) => {
              const isUnread = !n.readAt;
              const content = (
                <div
                  className={cn(
                    'flex gap-3 px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer group relative',
                    isUnread && 'bg-primary/5'
                  )}
                  onClick={() => handleNotificationClick(n)}
                >
                  {/* Severity indicator */}
                  <div className="shrink-0 mt-1">
                    <span className="text-lg">{TYPE_ICONS[n.type] || '🔔'}</span>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={cn(
                        'text-sm leading-snug',
                        isUnread ? 'font-medium text-foreground' : 'text-muted-foreground'
                      )}>
                        {n.title}
                      </p>
                      <span className="text-[11px] text-muted-foreground shrink-0">
                        {formatTimeAgo(n.createdAt)}
                      </span>
                    </div>
                    {n.body && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                        {n.body}
                      </p>
                    )}
                  </div>

                  {/* Unread dot */}
                  {isUnread && (
                    <div className={cn('w-2 h-2 rounded-full shrink-0 mt-2', SEVERITY_COLORS[n.severity] || 'bg-blue-500')} />
                  )}

                  {/* Dismiss button (on hover) */}
                  <button
                    onClick={(e) => handleDismiss(e, n.id)}
                    className="absolute top-2 ltr:right-2 rtl:left-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-muted"
                    aria-label={t('merchantNotifications.dismiss')}
                  >
                    <X className="w-3 h-3 text-muted-foreground" />
                  </button>
                </div>
              );

              return n.actionUrl ? (
                <Link key={n.id} href={n.actionUrl} className="block">
                  {content}
                </Link>
              ) : (
                <div key={n.id}>{content}</div>
              );
            })
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="border-t px-4 py-2 flex items-center justify-between">
            <Link
              href="/notifications"
              className="flex items-center gap-1.5 text-xs text-primary hover:underline py-1"
              onClick={() => setOpen(false)}
            >
              {t('merchantNotifications.viewAll')}
              <ExternalLink className="w-3 h-3" />
            </Link>
            <Link
              href="/settings/notifications"
              className="text-xs text-muted-foreground hover:text-foreground py-1"
              onClick={() => setOpen(false)}
            >
              {t('merchantNotifications.settings.title')}
            </Link>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
